import React, { useMemo, useState } from 'react';
import { useObservable } from 'react-use-observable';

import { UserOutlined as AvatarSolid, CloseOutlined as CancelIcon } from '@ant-design/icons';
import { Modal, Spin } from 'antd';

import { affiliateCommissionsService } from 'v2/services/reports/affiliateCommissions';

import { Autocomplete } from './Autocomplete';
import type { IAutocompleteOption } from './Autocomplete';
import { Table } from './Table';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const DetailsModal = ({ isOpen, handleClose }: IProps) => {
  const [selectedOptions, setSelectedOptions] = useState<IAutocompleteOption[]>([]);

  const [details] = useObservable(() => affiliateCommissionsService.getAffiliateCommissionsDetails(), []);
  const [loadingPartnerCommissionsDetails] = useObservable(
    () => affiliateCommissionsService.getLoadingAffiliateCommissionsDetails(),
    []
  );

  const handleAutoCompleteOnChange = (event: React.ChangeEvent<Element>, value: IAutocompleteOption[]) => {
    setSelectedOptions(value);
  };

  const autocompleteOptions: IAutocompleteOption[] = useMemo(() => {
    return details?.commissions?.map(commission => {
      const { product } = commission;

      return {
        value: product.id,
        label: `${product.id} - ${product.title}`
      };
    });
  }, [details]);

  const filteredCommissions = useMemo(() => {
    const selectedOptionsProductIds = selectedOptions.map(option => option.value);

    if (selectedOptionsProductIds.length) {
      return details?.commissions?.filter(commission => {
        const { product } = commission;

        return selectedOptionsProductIds.includes(product.id);
      });
    }

    return details?.commissions;
  }, [details, selectedOptions]);

  const handleDialogClose = () => {
    handleClose();
    setSelectedOptions([]);
  };

  return (
    <Modal
      className='max-w-[670px] [&_.ant-modal-body]:max-h-[524px]'
      open={isOpen}
      onCancel={handleDialogClose}
      width={670}
      footer={null}
      title='Mais detalhes'
      closeIcon={<CancelIcon />}
    >
      {loadingPartnerCommissionsDetails && (
        <div className='flex items-center justify-center p-8'>
          <Spin size='large' />
        </div>
      )}
      {!loadingPartnerCommissionsDetails && (
        <div>
          <header className='flex items-center rounded-sm bg-[#ECEFF1] px-[17px] py-3'>
            <AvatarSolid className='mr-2' />
            <span className='font-semibold'>
              Afiliado: <span className='text-grey-500 font-normal'>{details?.commissioned_name}</span>
            </span>
          </header>

          {details?.commissions?.length > 3 && (
            <Autocomplete
              options={autocompleteOptions}
              selectedOptions={selectedOptions}
              onChange={handleAutoCompleteOnChange}
            />
          )}

          <Table commissions={filteredCommissions} />
        </div>
      )}
    </Modal>
  );
};
